import React from 'react';
import './LandingPage.css';
import Seo from '../global/Seo';
import {
  PLANS,
  GLOBAL_PLAN_ORDER,
  ADD_ONS,
} from '../settings/ManageCompany/wizardConfig';
import {
  Check,
  Chevron,
  Members,
  Employees,
  Reporting,
  Payouts,
  ShieldPlain,
  LocationPin,
  Analytics,
  Permissions,
  Monitoring,
  Headset,
  Phone,
  ShieldCheck,
  Target,
  Home,
  ListGrid,
  CardIcon,
  // Desktop, Monitor, Keyboard, Camera, RfidCard, RfidReader, Tablet — used
  // only by the New Location hardware list, temporarily commented out.
  // Desktop,
  // Monitor,
  // Keyboard,
  // Camera,
  // RfidCard,
  // RfidReader,
  // Tablet,
} from './icons';

/**
 * Public marketing landing page served at "/" for unauthenticated visitors.
 * UI + icons mirror the approved prototype. Pricing is driven by the SAME
 * wizardConfig as Create Company (Alpha excluded) so it always matches.
 * All CTAs navigate to the /get-started onboarding page; "Sign In" → /login.
 */

const LOGO = `${process.env.PUBLIC_URL || ''}/nube-data.svg`;

const HERO_FEATS = [
  { icon: <Members />, label: 'Memberships' },
  { icon: <Employees />, label: 'Employees' },
  { icon: <Reporting />, label: 'Reporting' },
  { icon: <Payouts />, label: 'Payouts' },
  { icon: <ShieldPlain />, label: 'Security' },
  { icon: <LocationPin />, label: 'Multi-Locations' },
  { icon: <Analytics />, label: 'Analytics' },
  { icon: <Permissions />, label: 'Permissions' },
];

const TRUST = [
  { icon: <Headset />, label: '24/7 Live Support' },
  { icon: <Monitoring />, label: 'Real-Time Monitoring' },
  { icon: <CardIcon />, label: 'Thousands of Daily Transactions' },
  { icon: <ShieldPlain />, label: 'Trusted by Game Room Operators' },
];

const WHY = [
  {
    icon: <Phone />,
    title: 'Run Everything From Your Phone',
    body:
      "Check your floor, staff, and numbers from anywhere — live and online. No driving in to see what's happening.",
    points: [
      'Live dashboard on any device',
      'Verify staff & sessions remotely',
      'Your reports, in your pocket',
    ],
  },
  {
    icon: <ShieldCheck />,
    title: 'Prevent Internal Theft',
    body:
      'Tight books mean the cash stops disappearing. NuVe flags shortfalls and risky activity automatically.',
    points: [
      'Know who comes up short',
      'Over-ticket & match alerts',
      "Audit trail that can't be bypassed",
    ],
  },
  {
    icon: <Headset />,
    title: '24/7 Support That Actually Answers',
    body:
      'Other systems leave you waiting days for a callback. With NuVe, someone always answers and resolves your problem in under 24 hours.',
    points: [
      'Someone always answers',
      'Resolved in under 24 hours',
      'No callbacks days later',
    ],
  },
  {
    icon: <Target />,
    title: 'Built Specifically For Game Rooms',
    body:
      'Not generic POS software. Every feature is designed for how game rooms actually run, day to day.',
    points: [
      'Memberships & match tracking',
      'Machine readings & ticket-out',
      'Multi-location ready',
    ],
  },
];

const FEATURES = [
  {
    icon: <Home />,
    title: 'Control Center',
    items: [
      'Dashboard',
      'Customer Registration',
      'Customer Transactions',
      'Ticket Out',
      'Bonus',
      'Notes',
      'Raffles',
    ],
  },
  {
    icon: <ListGrid />,
    title: 'Readings',
    items: [
      'Employee Sessions',
      'Reading Sessions',
      'Machine Readings',
      'Reading History',
    ],
  },
  {
    icon: <CardIcon />,
    title: 'Accounting',
    items: [
      'Weekly Reports',
      'Loan Reports',
      'Employee Loan Reports',
      'Zelle Reports',
    ],
  },
  {
    icon: <ShieldPlain />,
    title: 'Security',
    items: [
      'Blacklist',
      'Match Privileges',
      'Fraud Detection',
      'NFC Ticket-Out',
      'Inactive Customer Tracking',
    ],
  },
];

const TESTIMONIALS = [
  {
    av: 'K',
    name: 'Kim',
    who: 'Owner · Houston, TX',
    quote:
      'Since switching to NuVe I finally see every location from my phone. The shortfall alerts paid for themselves in the first week.',
  },
  {
    av: 'M',
    name: 'Marcos',
    who: 'Manager · Multi-location',
    quote:
      'Support actually answers. We had an issue late at night and someone fixed it before close. That never happened with our old system.',
  },
  {
    av: 'K',
    name: 'Karla',
    who: 'Operations',
    quote:
      'Setup was painless and my staff learned it in a day. The match tracking makes theft almost impossible now.',
  },
];

const FAQ = [
  {
    q: 'What is NuVe System?',
    a: 'A cloud platform built specifically for game room operators to manage memberships, employees, machine readings, reporting, payouts, and security across one or many locations.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — talk to us about getting started. No setup fees, and you can cancel anytime.',
  },
  {
    q: 'Do I need special hardware?',
    a: "No special hardware is required to start. If you're opening a new location, we offer an optional one-time setup package with an example hardware list.",
  },
  {
    q: 'How does support work?',
    a: 'Real people, around the clock. Someone always answers and stays connected until your issue is resolved.',
  },
  {
    q: 'Can I manage multiple locations?',
    a: 'Yes. Premium and Pro Elite give you multi-location visibility from one owner-level dashboard.',
  },
  {
    q: 'Is my data secure?',
    a: "Yes. NuVe includes audit trails, blacklist, fraud detection, and role-based permissions that can't be bypassed.",
  },
];

// Used only by the New Location section, which is temporarily commented out.
/*
const NL_INCLUDED = [
  'Location Setup',
  'Profile & Config',
  'Manager Assignment',
  'Employee Access',
  'Reporting Enabled',
  'Ops Structure',
];
*/

// Compare-plans rows derived from the SAME wizardConfig plan sections used by
// Create Company, so the table always matches the plans/features exactly.
// A plan "has" a category when it defines a section with that title; the cell
// note is the section's own note ("All Standard features", "All Features
// Included") when present, otherwise just a check.
const COMPARE_CATEGORIES = (() => {
  const order = [];
  GLOBAL_PLAN_ORDER.forEach((pid) => {
    (PLANS[pid].sections || []).forEach((sec) => {
      if (!order.includes(sec.title)) order.push(sec.title);
    });
  });
  return order;
})();

function planCmp(pid, title) {
  const sec = (PLANS[pid].sections || []).find((s) => s.title === title);
  if (!sec) return false;
  if (sec.note) return sec.note;
  return true;
}

const COMPARE_ROWS = [
  ...COMPARE_CATEGORIES.map((title) => ({
    f: title,
    s: planCmp('standard', title),
    p: planCmp('premium', title),
    e: planCmp('pro_elite', title),
  })),
  // Marketing row (not a wizard feature category) — every plan includes it.
  { f: '24/7 Support', s: true, p: true, e: true },
];

// Used only by the New Location section, which is temporarily commented out.
/*
const NL_HARDWARE = [
  { icon: <Desktop />, name: 'Acer Aspire Desktop', spec: 'Win 11 · 8GB · 512GB', price: '$380' },
  { icon: <Monitor />, name: 'Acer 23.8" FHD Monitor', spec: '1080p IPS', price: '$120' },
  { icon: <Keyboard />, name: 'Logitech MK235', spec: 'Wireless KB & Mouse', price: '$28' },
  { icon: <Camera />, name: 'Logitech Brio 4K', spec: 'Noise-Cancel Mic', price: '$159' },
  { icon: <RfidCard />, name: 'RFID Cards (50 Pk)', spec: '13.56MHz Smart', price: '$25' },
  { icon: <RfidReader />, name: 'RFID USB Reader', spec: '13.56MHz Reader', price: '$27' },
  { icon: <Tablet />, name: 'onn 10.1" Tablet', spec: '32GB · Android', price: '$99' },
  { icon: <Keyboard />, name: 'Portable Keyboard', spec: 'Bluetooth / USB', price: '$25' },
];
*/

// Sections tracked by the nav for scroll + active highlight, in DOM order.
const NAV_SECTIONS = [
  'nv-why',
  'nv-features',
  'nv-pricing',
  'nv-faq',
  // 'nv-setup', // New Location section temporarily commented out
  'nv-final',
];

// Comparison-table cell: true → check; string → check + note; false → dash.
function renderCmpCell(val) {
  if (val === true) {
    return (
      <span className="nv-yes">
        <Check />
      </span>
    );
  }
  if (val === false) {
    return <span className="nv-no">—</span>;
  }
  return (
    <>
      <span className="nv-yes">
        <Check />
      </span>
      <div className="nv-note">{val}</div>
    </>
  );
}

class LandingPage extends React.Component {
  state = {
    activeSection: null,
    mobileMenuOpen: false,
  };

  componentDidMount() {
    // The app scrolls inside .shipment-background (not the window), so we
    // listen in the capture phase to catch scroll events from any ancestor
    // scroll container.
    document.addEventListener('scroll', this.onScroll, true);
    this.onScroll();
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.onScroll, true);
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  // Get Started is now its own page (/get-started) so it never covers the
  // marketing content like the old modal did. A pre-selected plan rides along
  // as ?plan=<id>.
  openWizard = (planId) => {
    const url = `/get-started${planId ? `?plan=${planId}` : ''}`;
    if (this.props.history) this.props.history.push(url);
    else window.location.assign(url);
  };

  goLogin = () => {
    if (this.props.history) this.props.history.push('/login');
    else window.location.assign('/login');
  };

  // scrollIntoView scrolls the nearest scrollable ancestor regardless of
  // whether that's the window or .shipment-background; scroll-margin-top on
  // .nv-section keeps the target clear of the sticky nav.
  scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  scrollHandler = (id) => (e) => {
    if (e) e.preventDefault();
    this.scrollTo(id);
  };

  loginHandler = (e) => {
    if (e) e.preventDefault();
    this.goLogin();
  };

  toggleMenu = () =>
    this.setState((s) => ({ mobileMenuOpen: !s.mobileMenuOpen }));

  closeMenu = () => this.setState({ mobileMenuOpen: false });

  // Mobile-menu link: close the menu, then scroll to the section.
  mobileNav = (id) => (e) => {
    if (e) e.preventDefault();
    this.closeMenu();
    this.scrollTo(id);
  };

  mobileLogin = (e) => {
    if (e) e.preventDefault();
    this.closeMenu();
    this.goLogin();
  };

  mobileWizard = (e) => {
    if (e) e.preventDefault();
    this.closeMenu();
    this.openWizard();
  };

  // Scrollspy: the active section is the last one whose top has passed under
  // the sticky nav. getBoundingClientRect is viewport-relative so this works
  // no matter which element actually scrolls.
  onScroll = () => {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      const threshold = 120;
      // Active = the section whose top is closest to (just under) the nav,
      // i.e. the greatest top that has passed the threshold. This is
      // independent of the order ids are listed in, so it can't mis-highlight.
      let current = null;
      let bestTop = -Infinity;
      NAV_SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top <= threshold && top > bestTop) {
          bestTop = top;
          current = id;
        }
      });
      if (current !== this.state.activeSection) {
        this.setState({ activeSection: current });
      }
    });
  };

  navClass = (id) =>
    `nv-navlink-hide${this.state.activeSection === id ? ' nv-active' : ''}`;

  render() {
    return (
      <div className="nv-landing">
        <Seo
          title="Management Platform for Game Rooms"
          description="NuVe System — the #1 platform for game rooms. Manage memberships, employees, machine readings, reporting, payouts, security, and multiple locations from one dashboard."
          path="/"
        />
        {/* NAV */}
        <header className="nv-header">
          <div className="nv-wrap">
            <nav className="nv-nav">
              <div className="nv-brand" onClick={this.goLogin}>
                <img src={LOGO} alt="NuVe System" />
                NuVe System
              </div>
              <div className="nv-navlinks">
                <a className={this.navClass('nv-why')} href="#nv-why" onClick={this.scrollHandler('nv-why')}>
                  Why NuVe
                </a>
                <a className={this.navClass('nv-features')} href="#nv-features" onClick={this.scrollHandler('nv-features')}>
                  Features
                </a>
                <a className={this.navClass('nv-pricing')} href="#nv-pricing" onClick={this.scrollHandler('nv-pricing')}>
                  Pricing
                </a>
                <a className={this.navClass('nv-faq')} href="#nv-faq" onClick={this.scrollHandler('nv-faq')}>
                  FAQ
                </a>
                {/* New Location nav link hidden while the section is commented out
                <a className={this.navClass('nv-setup')} href="#nv-setup" onClick={this.scrollHandler('nv-setup')}>
                  New Location
                </a>
                */}
                <a className={this.navClass('nv-final')} href="#nv-final" onClick={this.scrollHandler('nv-final')}>
                  Contact
                </a>
                <a className="nv-signin" href="/login" onClick={this.loginHandler}>
                  Sign In
                </a>
              </div>
              <button
                className={`nv-burger${this.state.mobileMenuOpen ? ' nv-x' : ''}`}
                aria-label="Menu"
                aria-expanded={this.state.mobileMenuOpen}
                onClick={this.toggleMenu}
              >
                <span />
                <span />
                <span />
              </button>
            </nav>
          </div>

          <div
            className={`nv-mobile-menu${
              this.state.mobileMenuOpen ? ' nv-open' : ''
            }`}
          >
            <a href="#nv-why" onClick={this.mobileNav('nv-why')}>
              Why NuVe
            </a>
            <a href="#nv-features" onClick={this.mobileNav('nv-features')}>
              Features
            </a>
            <a href="#nv-pricing" onClick={this.mobileNav('nv-pricing')}>
              Pricing
            </a>
            <a href="#nv-faq" onClick={this.mobileNav('nv-faq')}>
              FAQ
            </a>
            {/* New Location nav link hidden while the section is commented out
            <a href="#nv-setup" onClick={this.mobileNav('nv-setup')}>
              New Location
            </a>
            */}
            <a href="#nv-final" onClick={this.mobileNav('nv-final')}>
              Contact
            </a>
            <a href="#get-started" className="nv-mm-cta" onClick={this.mobileWizard}>
              Get Started
            </a>
            <a href="/login" className="nv-mm-signin" onClick={this.mobileLogin}>
              Sign In
            </a>
          </div>
        </header>
        <div
          className={`nv-menu-backdrop${
            this.state.mobileMenuOpen ? ' nv-open' : ''
          }`}
          onClick={this.closeMenu}
        />

        {/* HERO */}
        <section className="nv-hero">
          <div className="nv-hx">
            <div className="nv-badge">
              <span className="nv-dot" />
              THE #1 PLATFORM FOR GAME ROOMS
            </div>
            <h1 className="nv-h1">
              NuVe System — The #1 Management Platform for{' '}
              <span className="nv-hl">Game Rooms</span>
            </h1>
            <p className="nv-hx-es">La plataforma #1 para administrar game rooms.</p>
            <p className="nv-hx-desc">
              Manage memberships, employees, reporting, payouts, security,
              multiple locations, analytics, and operations from one easy-to-use
              platform.
            </p>
            <p className="nv-hx-desc-es">
              Administra membresías, empleados, reportes, pagos, seguridad y
              múltiples ubicaciones desde una sola plataforma.
            </p>

            <div className="nv-hx-feats">
              {HERO_FEATS.map((f) => (
                <div className="nv-hx-feat" key={f.label}>
                  {f.icon}
                  <span>{f.label}</span>
                </div>
              ))}
            </div>

            <div className="nv-cta">
              <button className="nv-btn nv-btn-blue" onClick={() => this.openWizard()}>
                Start Free Trial
              </button>
              <button
                className="nv-btn nv-btn-primary"
                onClick={() => this.openWizard()}
              >
                Comienza tu prueba gratis
              </button>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <div className="nv-trust">
          <div className="nv-wrap">
            {TRUST.map((t) => (
              <div className="nv-trust-it" key={t.label}>
                {t.icon}
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* WHY */}
        <section id="nv-why" className="nv-section nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">Why operators switch</div>
            <h2 className="nv-sec-title">Why Operators Switch to NuVe</h2>
            <p className="nv-sec-sub">
              Four reasons game room owners move to NuVe — and stay.
            </p>
            <div className="nv-grid4">
              {WHY.map((c) => (
                <div className="nv-card" key={c.title}>
                  <div className="nv-ic">{c.icon}</div>
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                  <ul>
                    {c.points.map((pt) => (
                      <li key={pt}>
                        <Check />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section id="nv-features" className="nv-section nv-features nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">Features</div>
            <h2 className="nv-sec-title">Everything Your Game Room Needs</h2>
            <p className="nv-sec-sub">
              Organized into four simple categories — no clutter.
            </p>
            <div className="nv-grid4">
              {FEATURES.map((cat) => (
                <div className="nv-card" key={cat.title}>
                  <div className="nv-cat-h">
                    <span className="nv-cat-ic">{cat.icon}</span>
                    <b>{cat.title}</b>
                  </div>
                  <ul>
                    {cat.items.map((it) => (
                      <li key={it}>
                        <Check />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="nv-section nv-testi nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">Testimonials</div>
            <h2 className="nv-sec-title">Loved by Game Room Operators</h2>
            <p className="nv-sec-sub">Real results from operators running game rooms.</p>
            <div className="nv-tst-grid">
              {TESTIMONIALS.map((t) => (
                <div className="nv-tst" key={t.name}>
                  <div className="nv-stars">★★★★★</div>
                  <p>{t.quote}</p>
                  <div className="nv-who">
                    <span className="nv-av">{t.av}</span>
                    <div>
                      <b>{t.name}</b>
                      <span>{t.who}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING — unchanged (wizardConfig-driven, no Alpha) */}
        <section id="nv-pricing" className="nv-section nv-pricing nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">Pricing</div>
            <h2 className="nv-sec-title">Simple, Transparent Pricing</h2>
            <p className="nv-sec-sub">Pick a plan, scale as you grow.</p>
            <div className="nv-free-pill">
              <span role="img" aria-label="gift">
                🎁
              </span>{' '}
              First month FREE on all plans
            </div>
            <div className="nv-px-grid">
              {GLOBAL_PLAN_ORDER.map((pid) => {
                const p = PLANS[pid];
                return (
                  <div
                    key={pid}
                    className={`nv-px ${p.badge ? 'nv-pop' : ''}`}
                    style={{ '--plan-color': p.color }}
                  >
                    <div className="nv-px-name">
                      {p.name}
                      {p.badge && <span className="nv-px-pop">★ {p.badge}</span>}
                    </div>
                    <div className="nv-px-price">
                      <span className="nv-amt">${p.price}</span>
                      <span className="nv-per">/mo</span>
                    </div>
                    <button
                      className={`nv-btn ${p.badge ? 'nv-btn-primary' : 'nv-btn-blue'}`}
                      onClick={() => this.openWizard(pid)}
                    >
                      Get Started
                    </button>
                    {p.sections.map((sec) => (
                      <div className="nv-px-grp" key={sec.title}>
                        <h4>{sec.title}</h4>
                        {sec.note ? (
                          <div className="nv-all">{sec.note}</div>
                        ) : (
                          <ul>
                            {(sec.items || []).map((it) => (
                              <li key={it}>{it}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 56 }}>
              <h2 className="nv-sec-title" style={{ fontSize: 'clamp(20px,3vw,30px)' }}>
                Optional Add-Ons
              </h2>
              <p className="nv-sec-sub">Extend any plan with the modules you need.</p>
              <div className="nv-addons">
                {ADD_ONS.map((a) => (
                  <div className="nv-ad" key={a.id}>
                    <b>{a.name}</b>
                    <div className="nv-ad-p">+${a.price}/mo</div>
                  </div>
                ))}
              </div>
            </div>

            <p className="nv-px-foot">
              First month free on all plans · billed monthly · no setup fees ·
              cancel anytime.
            </p>

            {/* Compare Plans */}
            <div style={{ marginTop: 56 }}>
              <h2
                className="nv-sec-title"
                style={{ fontSize: 'clamp(20px,3vw,30px)' }}
              >
                Compare Plans
              </h2>
            </div>
            <div className="nv-cmp-wrap">
              <table className="nv-cmp">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Standard</th>
                    <th className="nv-pop-th">Premium</th>
                    <th>Pro Elite</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row) => (
                    <tr key={row.f}>
                      <td>{row.f}</td>
                      <td>{renderCmpCell(row.s)}</td>
                      <td className="nv-popcell">{renderCmpCell(row.p)}</td>
                      <td>{renderCmpCell(row.e)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="nv-faq" className="nv-section nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">FAQ</div>
            <h2 className="nv-sec-title">Frequently Asked Questions</h2>
            <div className="nv-faq">
              {FAQ.map((f) => (
                <details key={f.q}>
                  <summary>
                    {f.q}
                    <span className="nv-chev">
                      <Chevron />
                    </span>
                  </summary>
                  <div className="nv-a">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* NEW LOCATION — temporarily hidden (commented out for now) */}
        {/*
        <section id="nv-setup" className="nv-section nv-setup nv-center">
          <div className="nv-wrap">
            <div className="nv-eyebrow">New Location</div>
            <h2 className="nv-sec-title">New Location Setup Package</h2>
            <p className="nv-sec-sub">
              Everything you need to launch a new location — structured from day
              one.
            </p>
            <div className="nv-nl-inc">
              {NL_INCLUDED.map((x) => (
                <div key={x}>
                  <Check />
                  {x}
                </div>
              ))}
            </div>

            <div className="nv-nl-card">
              <h3>Standard Hardware Budget</h3>
              <div className="nv-nl-grid">
                {NL_HARDWARE.map((h) => (
                  <div className="nv-nl-item" key={h.name}>
                    <div className="nv-nl-ic">{h.icon}</div>
                    <b>{h.name}</b>
                    <span>{h.spec}</span>
                    <div className="nv-nl-pr">{h.price}</div>
                  </div>
                ))}
              </div>
              <div className="nv-nl-tot">
                <div className="nv-nl-r">
                  <span>Subtotal</span>
                  <span>$863.00</span>
                </div>
                <div className="nv-nl-r">
                  <span>Houston Sales Tax (8.25%)</span>
                  <span>$71.20</span>
                </div>
                <div className="nv-nl-grand">
                  <b>Grand Total</b>
                  <span className="nv-v">$934.20</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 22 }}>
              <button className="nv-btn nv-btn-blue" onClick={() => this.openWizard()}>
                Set Up My New Location
              </button>
            </div>
          </div>
        </section>
        */}

        {/* FINAL CTA */}
        <section id="nv-final" className="nv-section nv-final">
          <div className="nv-wrap">
            <h2>Ready to Modernize Your Game Room?</h2>
            <p>Start today — or talk to us for a quick demo.</p>
            <div className="nv-cta">
              <button className="nv-btn nv-btn-primary" onClick={() => this.openWizard()}>
                Get Started
              </button>
              <button className="nv-btn nv-btn-ghost" onClick={this.goLogin}>
                Sign In
              </button>
            </div>
            <p className="nv-final-contact">
              Call <a href="tel:+13464615222">(346) 461-5222</a> &nbsp;·&nbsp;{' '}
              <a href="https://nuvesystem.com" target="_blank" rel="noopener noreferrer">
                nuvesystem.com
              </a>
            </p>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="nv-footer">
          <div className="nv-wrap">
            <div className="nv-foot-grid">
              <div className="nv-foot-brand">
                <div className="nv-brand" onClick={this.goLogin}>
                  <img src={LOGO} alt="NuVe System" />
                  NuVe System
                </div>
                <p>Management Platform for Game Rooms</p>
                <p style={{ marginTop: 10 }}>
                  <a href="tel:+13464615222">(346) 461-5222</a>
                  <br />
                  <a href="https://nuvesystem.com" target="_blank" rel="noopener noreferrer">
                    nuvesystem.com
                  </a>
                </p>
              </div>
              <div className="nv-foot-links">
                <div className="nv-foot-col">
                  <h5>Product</h5>
                  <a href="#nv-why" onClick={this.scrollHandler('nv-why')}>
                    Why NuVe
                  </a>
                  <a href="#nv-features" onClick={this.scrollHandler('nv-features')}>
                    Features
                  </a>
                  <a href="#nv-pricing" onClick={this.scrollHandler('nv-pricing')}>
                    Pricing
                  </a>
                  <a href="/login" onClick={this.loginHandler}>
                    Sign In
                  </a>
                </div>
                <div className="nv-foot-col">
                  <h5>Company</h5>
                  <a href="/privacy-policy">Privacy Policy</a>
                  <a href="/terms-and-conditions">Terms &amp; Conditions</a>
                  <a href="/sms-opt-in">SMS Opt-In</a>
                  <a href="#nv-final" onClick={this.scrollHandler('nv-final')}>
                    Contact
                  </a>
                </div>
              </div>
            </div>
            <div className="nv-foot-bottom">
              Copyright © NuVe System. All Rights Reserved.
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default LandingPage;
